import React, { useState, useEffect } from "react";
import Input from "../../components/Input";
import { SaveCancel } from "../../components/PrimaryButton";
import { securePut, api } from "../../config/api";
import { getResponseData } from '../../config/api';
import { getJwkKeystore } from '../../utils/encryption';
import { useTranslation } from "react-i18next";
import { formatAmount } from "../../utils/format";
import SnackbarNotification from '../../components/SnackbarNotification';

import DeleteIcon from "../../icons/trashR.svg";
import PlusIcon from "../../icons/plus.svg";
import checkIcon from "../../icons/check-square.svg";

const cleanDecimal = (raw) => {
  if (raw == null) return "";
  let v = String(raw)
    .replace(/[^0-9,.]/g, "")
    .replace(/,/g, ".")
    .replace(/\.(?=.*\.)/g, "") 
    .replace(/^\./, "0.");     
  const m = v.match(/^(\d+)(?:\.(\d{0,2}))?$/);
  if (!m) {
    if (v === "" || v === "0.") return v;
    const [intPart, decPart = ""] = v.split(".");
    return decPart ? `${intPart}.${decPart.slice(0, 2)}` : intPart;
  }
  return v;
};

const ProductForm = ({
  title,
  initialValues = {},
  baseFields,
  familyAndBrandFields,
  quantityFields,
  additionalFields,
  fileFields,
  onSubmit,
  onSuccess,
  onCancel,
  apiUrl,
}) => {
  const [formValues, setFormValues] = useState(initialValues);
  const [dynamicEntries, setDynamicEntries] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [files, setFiles] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [previews, setPreviews] = useState({});
  const [additionalValues, setAdditionalValues] = useState({});
  const [uploadedImage,] = useState(initialValues.image || "");
  const [imageChanged, setImageChanged] = useState(false);
  const { t } = useTranslation();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [saving, setSaving] = useState(false);
  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const tva = parseFloat(process.env.REACT_APP_TVA);

  useEffect(() => {
    if (initialValues) {
      setFormValues({
        ...initialValues,
        brandId: initialValues.brand?.id || "",
        familyId: initialValues.family?.id || "",
      });

      if (initialValues.discounts) {
        setDynamicEntries(
          initialValues.discounts.map((discount) => ({
            id: discount.id || "",
            quantity: discount.quantity || "",
            price: discount.price || "",
          }))
        );
      }

      if (initialValues.fields) {
        const mappedAdditionalFields = initialValues.fields.reduce((acc, field) => {
          acc[field.name] = field.value || "";
          return acc;
        }, {});
        setAdditionalValues(mappedAdditionalFields);
      }
    }
  }, [initialValues]);

  useEffect(() => {
    if (initialValues.image) {
      const match = initialValues.image.match(/grayfox\/([^?]+)/);
      if (match) {
        setFileNames((prev) => ({
          ...prev,
          logo: match[1],
        }));
      }
    }
  }, [initialValues.image]);

  const handleAdditionalChange = (name, value) => {
    setAdditionalValues((prev) => ({ ...prev, [name]: value }));
  };


  const handleAddQuantityEntry = () => {
    if (quantity && price) {
      setDynamicEntries((prev) => [...prev, { quantity, price }]);
      setQuantity("");
      setPrice("");
    }
  };

  const removeDynamicEntry = (index) => {
    setDynamicEntries((prev) => prev.filter((_, i) => i !== index));
  };

const handleInputChange = (name, value) => {
  const rate = Number.isFinite(tva) ? tva : 0;
  const v = cleanDecimal(value);

  if (name === "priceHT") {
    setFormValues(prev => ({ ...prev, priceHT: v }));
    const ht = parseFloat(v);
    if (!isNaN(ht)) {
      const ttc = (ht * (1 + rate)).toFixed(2);
      setFormValues(prev => ({ ...prev, priceTTC: ttc }));
    } else {
      setFormValues(prev => ({ ...prev, priceTTC: "" }));
    }
    return;
  }

  if (name === "priceTTC") {
    setFormValues(prev => ({ ...prev, priceTTC: v }));
    const ttc = parseFloat(v);
    if (!isNaN(ttc)) {
      const ht = (ttc / (1 + rate)).toFixed(2);
      setFormValues(prev => ({ ...prev, priceHT: ht }));
    } else {
      setFormValues(prev => ({ ...prev, priceHT: "" }));
    }
    return;
  }

  if (name === "priceConcessionHT") {
    setFormValues(prev => ({ ...prev, priceConcessionHT: v }));
    const ht = parseFloat(v);
    if (!isNaN(ht)) {
      const ttc = (ht * (1 + rate)).toFixed(2);
      setFormValues(prev => ({ ...prev, priceConcessionTTC: ttc }));
    } else {
      setFormValues(prev => ({ ...prev, priceConcessionTTC: "" }));
    }
    return;
  }

  if (name === "priceConcessionTTC") {
    setFormValues(prev => ({ ...prev, priceConcessionTTC: v }));
    const ttc = parseFloat(v);
    if (!isNaN(ttc)) {
      const ht = (ttc / (1 + rate)).toFixed(2);
      setFormValues(prev => ({ ...prev, priceConcessionHT: ht }));
    } else {
      setFormValues(prev => ({ ...prev, priceConcessionHT: "" }));
    }
    return;
  }

  setFormValues(prev => ({ ...prev, [name]: value }));
};


  const handleFileChange = (e) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviews(prev => ({ ...prev, [name]: previewUrl }));
      setFiles((prev) => ({ ...prev, [name]: file }));
      setFileNames((prev) => ({ ...prev, [name]: file.name }));
      setImageChanged(true);
    }
  };

  const removeFile = (fieldName) => {
    URL.revokeObjectURL(previews[fieldName]);
    setPreviews(prev => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
    setFiles((prev) => {
      const updatedFiles = { ...prev };
      delete updatedFiles[fieldName];
      return updatedFiles;
    });
    setFileNames((prev) => {
      const updatedNames = { ...prev };
      delete updatedNames[fieldName];
      return updatedNames;
    });
    setImageChanged(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = [...baseFields, ...familyAndBrandFields, ...quantityFields, ...fileFields, ...additionalFields];
    const missing = requiredFields.filter(f => f.required && !formValues[f.name]);
    if (missing.length > 0) {
      showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
      return;
    }
    let finalImageName = "";
    let includeImage = false;

    if (uploadedImage) {
      const match = uploadedImage.match(/grayfox\/([^?]+)/); 
      if (match) {
        finalImageName = match[1];
      }
    }

    if (imageChanged && files.logo) {
      const formData = new FormData();
      formData.append("file", files.logo);

      try {
        const fileUploadResponse = await api.post(
          "/attachments/images/add",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: localStorage.getItem("token"),
            },
          }
        );


        const key = await getJwkKeystore();
        const decryptedResponse = await getResponseData(fileUploadResponse, key);


        if (decryptedResponse && decryptedResponse.fileName) {
          finalImageName = decryptedResponse.fileName;
          includeImage = true;
        } else {
          throw new Error("File name not found in the decrypted response");
        }
      } catch (error) {
        console.error("Error uploading file:", error.response?.data || error);
        setSnackbar({
          open: true,
          message: t(`errors.generic`),
          severity: "error",
        });
        return;
      }
    }

    const formPayload = {
      ...(initialValues?.id && { id: initialValues.id }),
      name: formValues.name || "",
      reference: formValues.reference || "",
      priceHT: parseFloat(formValues.priceHT),
      priceTTC: Number(formValues.priceTTC),
      priceConcessionHT: parseFloat(formValues.priceConcessionHT),
      priceConcessionTTC: Number(formValues.priceConcessionTTC),
      discount: Number(formValues.discount) || null,
      salesDiscountLimit: Number(formValues.salesDiscountLimit) || null,
      brandId: Number(formValues.brandId),
      familyId: Number(formValues.familyId),
      discounts: dynamicEntries.map((entry) => ({
        id: entry.id,
        quantity: Number(entry.quantity),
        price: Number(entry.price),
      })),
      fields: { ...additionalValues },
      tva: tva,
    };

    if (includeImage) {
      formPayload.image = finalImageName;
    } else if (!imageChanged && finalImageName) {
      formPayload.image = finalImageName;
    }

    setSaving(true);
    try {
      const response = await securePut(apiUrl, formPayload, showSnackbar);

      onSubmit(response.data);
      onSuccess();

    } catch (error) {
      console.error("Error during form submission:", error);
    } finally {
      setSaving(false);
    }
  };




  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
      <div className="bg-white rounded-[12px] shadow-lg w-full max-w-screen-lg max-h-[90vh] flex flex-col">
        <div className="p-6 pb-0 sticky top-0 z-10">
          <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
        </div>
        <div className="overflow-y-auto px-8 py-6 space-y-6 flex-1">

          <form onSubmit={handleSubmit} className="space-y-6">

          {/* Base Fields */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {baseFields.map((field, index) => (
                  <Input
                    key={index}
                    label={field.label}
                    name={field.name}
                    type={field.type || "text"}
                    value={formValues[field.name] || ""}
                    required={field.required}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>


            {/* Family and Brand Fields */}
            <div className="space-y-4">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.brand&family`)}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {familyAndBrandFields.map((field, index) => (
                  <Input
                    key={index}
                    label={field.label}
                    name={field.name}
                    type={field.type || "text"}
                    options={field.options || []}
                    required={field.required}
                    value={formValues[field.name] || ""}
                    onChange={(value) => handleInputChange(field.name, value)}
                  />
                ))}
              </div>
            </div>

            {/* Quantity Fields */}
            <div className="space-y-4">
              <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.priceToQuantity`)}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 items-end">
                {quantityFields.map((field, index) => (
                  <Input
                    key={index}
                    label={field.label}
                    name={field.name}
                    type={field.type || "text"}
                    required={field.required}
                    value={field.name === "quantity" ? quantity : price}
                    onChange={(value) =>
                      field.name === "quantity" ? setQuantity(value) : setPrice(value)
                    }
                  />
                ))}
                <button
                  type="button"
                  onClick={handleAddQuantityEntry}
                  className="bg-[#043F88] text-white rounded-lg px-4 py-2 w-[48px] h-[48px] flex items-center justify-center"
                >
                  <img src={PlusIcon} alt="Add" />
                </button>
              </div>

              {/* Dynamic Entries */}
              {dynamicEntries.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {dynamicEntries.map((entry, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white shadow-[0px_20px_80px_#00000011] rounded-lg p-4 border border-gray-200 w-full"
                    >
                      <div className="flex-1">
                        <p className="text-[10px] font-semibold text-gray-500">{t(`rows.quantity`).toUpperCase()}</p>
                        <p className="text-base font-bold text-gray-800">{entry.quantity}</p>
                      </div>
                      <div className="flex-1">
                        <p className="text-[10px] font-semibold text-gray-500">{t(`rows.priceuTTC`).toUpperCase()}</p>
                        <p className="text-base font-bold text-gray-800">{formatAmount(entry.price)}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeDynamicEntry(index)}
                        className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
                      >
                        <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Additional Fields */}
            {additionalFields.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.additional`)}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  {additionalFields.map((field, index) => (
                    <Input
                      key={index}
                      label={field.label}
                      name={field.name}
                      type={field.type}
                      required={field.required}
                      value={additionalValues[field.name] || ""}
                      onChange={(value) => handleAdditionalChange(field.name, value)}
                      options={field.options || []}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* File Field */}
            {fileFields && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  {fileFields.map((field, index) => {
                    const hasFile = Boolean(fileNames[field.name]);
                    const existingImage = initialValues.image;
                    const previewImage = previews[field.name];

                    return (
                      <div key={index} className="space-y-2">
                        <label
                          htmlFor={`fileInput-${field.name}`}
                          className={` border-2 border-dashed h-[93px] rounded-md p-4 block cursor-pointer transition-colors
                                ${hasFile ? 'border-gray-300' : 'border-gray-300'} `}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const droppedFile = e.dataTransfer.files?.[0];
                            if (droppedFile) {
                              const validTypes = field.accept.split(',').map(type => type.trim());
                              const isValid = validTypes.some(type => {
                                if (type.endsWith('/*')) {
                                  const [mainType] = type.split('/');
                                  return droppedFile.type.startsWith(`${mainType}/`);
                                }
                                return droppedFile.type === type;
                              });

                              if (isValid) {
                                handleFileChange({
                                  target: {
                                    name: field.name,
                                    files: [droppedFile]
                                  }
                                });
                              } else {
                                showSnackbar(t('error.invalidFileType', { types: field.accept }), 'error');
                              }
                            }
                          }}
                        >
                          <input
                            id={`fileInput-${field.name}`}
                            type="file"
                            name={field.name}
                            accept={field.accept}
                            className="hidden"
                            onChange={(e) => {
                              const selectedFile = e.target.files?.[0];
                              if (selectedFile) {
                                if (
                                  field.accept.includes(selectedFile.type) ||
                                  (field.accept === "image/*" && selectedFile.type.startsWith("image/"))
                                ) {
                                  handleFileChange(e);
                                }
                              }
                            }} />
                          <div className="flex items-center justify-between gap-3 h-full min-h-[40px]">
                            {hasFile ? (
                              <>
                                <div className="flex items-center ml-[40px] min-w-0 flex-1 h-full">
                                  {(previewImage || existingImage) && (
                                    <div className="relative w-20 h-20 shrink-0">
                                      <img
                                        src={previewImage || existingImage}
                                        alt="Preview"
                                        className="w-full h-full object-contain rounded-md"
                                      />
                                    </div>
                                  )}
                                  <span className="text-[14px] font-semibold ml-[20px] text-[#38475D] truncate">
                                    {fileNames[field.name]}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    removeFile(field.name);
                                  }}
                                className="w-8 h-8 p-1 -m-1 mr-[36px] flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                >
                                  <img src={DeleteIcon} alt="Delete" className="w-5 h-5" />
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center justify-center w-full gap-4 text-center">
                                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <div className="text-left">
                                  <p className="text-sm text-gray-500">{field.placeholderText1}</p>
                                  <p className="text-sm font-bold text-blue-500">{field.placeholderText2}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </form>
        </div>
        {/* Save/Cancel Buttons */}
        <div className="p-6 pt-0  sticky bottom-0z-10 flex justify-end space-x-4">
          <SaveCancel onCancelClick={onCancel} onSaveClick={handleSubmit} saving={saving} />
        </div>

      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default ProductForm;
